import { resolvePageCustomCodeSlots } from "../ui/lib/pageCustomCodeSlots";
import {
	GOOGLE_FONT_WEIGHTS,
	GOOGLE_FONTS_CATALOG,
	type GoogleFontWeightSpec,
} from "../ui/lib/recommendedFonts";
import type { BuilderThemeTypography } from "./tenantThemeCss";
import {
	buildTypographyStylesByViewport,
	isLegacyDefaultSerifFontFamily,
	isSiteSansFontFamilyToken,
	splitCssFontFamilyList,
	TYPOGRAPHY_BREAKPOINT_ORDER,
	TYPOGRAPHY_ELEMENT_ORDER,
} from "./typographyTokens";

export type GoogleFontFamilyRequest = {
	family: string;
	weights: number[];
	italic: boolean;
};

const CATALOG_BY_LOWER = new Map(
	GOOGLE_FONTS_CATALOG.map((family) => [family.toLowerCase(), family])
);

function stripFontQuotes(value: string): string {
	const trimmed = value.trim();
	if (
		(trimmed.startsWith('"') && trimmed.endsWith('"')) ||
		(trimmed.startsWith("'") && trimmed.endsWith("'"))
	) {
		return trimmed.slice(1, -1).trim();
	}
	return trimmed;
}

/** Case-insensitive catalog match after quotes are stripped. Local faces return null. */
export function matchCatalogGoogleFontFamily(part: string): string | null {
	const bare = stripFontQuotes(part);
	if (!bare || /^var\(/i.test(bare)) {
		return null;
	}
	return CATALOG_BY_LOWER.get(bare.toLowerCase()) ?? null;
}

function catalogFamiliesInList(value: string): string[] {
	const found: string[] = [];
	for (const part of splitCssFontFamilyList(value)) {
		const hit = matchCatalogGoogleFontFamily(part);
		if (hit && !found.includes(hit)) {
			found.push(hit);
		}
	}
	return found;
}

function familiesForElementFont(
	fontFamily: string | undefined,
	fontSans: string | null
): string[] {
	const raw = fontFamily?.trim() ?? "";
	if (
		!raw ||
		isSiteSansFontFamilyToken(raw) ||
		isLegacyDefaultSerifFontFamily(raw)
	) {
		return fontSans ? catalogFamiliesInList(fontSans) : [];
	}
	return catalogFamiliesInList(raw);
}

function parseUsedWeight(raw: string | undefined): number | null {
	if (!raw) {
		return null;
	}
	const trimmed = raw.trim().toLowerCase();
	if (trimmed === "normal") {
		return 400;
	}
	if (trimmed === "bold") {
		return 700;
	}
	const parsed = Number.parseInt(trimmed, 10);
	return Number.isFinite(parsed) ? parsed : null;
}

type FamilyUse = {
	weights: Set<number>;
	italic: boolean;
};

function ensureFamily(map: Map<string, FamilyUse>, family: string): FamilyUse {
	let use = map.get(family);
	if (!use) {
		use = { weights: new Set<number>(), italic: false };
		map.set(family, use);
	}
	return use;
}

function recordFamilyUse(
	map: Map<string, FamilyUse>,
	families: readonly string[],
	weight: number | null,
	italic: boolean
): void {
	for (const family of families) {
		const use = ensureFamily(map, family);
		if (weight !== null) {
			use.weights.add(weight);
		}
		if (italic) {
			use.italic = true;
		}
	}
}

function supportedWeights(
	spec: GoogleFontWeightSpec,
	used: Set<number>
): number[] {
	const matched = [...used]
		.filter((weight) => spec.weights.includes(weight))
		.sort((a, b) => a - b);
	if (matched.length > 0) {
		return matched;
	}
	if (spec.weights.includes(400)) {
		return [400];
	}
	const first = spec.weights[0];
	return first === undefined ? [] : [first];
}

/**
 * Catalog Google families referenced by a typography set. Local faces
 * (Georgia, Segoe UI, system-ui) never match. Weights are the ones actually
 * used, intersected with what that family supports.
 */
export function collectGoogleFontFamilies(
	typography: BuilderThemeTypography
): GoogleFontFamilyRequest[] {
	const map = new Map<string, FamilyUse>();
	for (const stack of [
		typography.fontSans,
		typography.fontSerif,
		typography.fontMono,
	]) {
		if (!stack) {
			continue;
		}
		recordFamilyUse(map, catalogFamiliesInList(stack), 400, false);
	}

	const byVp = buildTypographyStylesByViewport(typography.elements);
	for (const viewport of TYPOGRAPHY_BREAKPOINT_ORDER) {
		for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
			const style = byVp[viewport][key];
			const families = familiesForElementFont(
				style.fontFamily,
				typography.fontSans
			);
			recordFamilyUse(
				map,
				families,
				parseUsedWeight(style.fontWeight),
				typeof style.fontStyle === "string" &&
					style.fontStyle.toLowerCase().includes("italic")
			);
		}
	}

	return requestsFromFamilyMap(map);
}

function requestsFromFamilyMap(
	map: Map<string, FamilyUse>
): GoogleFontFamilyRequest[] {
	const requests: GoogleFontFamilyRequest[] = [];
	for (const family of GOOGLE_FONTS_CATALOG) {
		const use = map.get(family);
		if (!use) {
			continue;
		}
		const spec = GOOGLE_FONT_WEIGHTS[family];
		if (!spec) {
			continue;
		}
		const weights = supportedWeights(spec, use.weights);
		if (weights.length === 0) {
			continue;
		}
		requests.push({
			family,
			weights,
			italic: use.italic && spec.italic,
		});
	}
	return requests;
}

const CSS_COMMENT = /\/\*[\s\S]*?\*\//g;
const FONT_FAMILY_DECL = /(?:^|[{;\s])font-family\s*:\s*([^;{}]+)/gi;
const FONT_WEIGHT_DECL = /(?:^|[{;\s])font-weight\s*:\s*([^;{}]+)/i;
const FONT_STYLE_ITALIC = /(?:^|[{;\s])font-style\s*:\s*(?:italic|oblique)\b/i;
const INNERMOST_BLOCK = /\{([^{}]*)\}/g;
const HTML_STYLE_BLOCK = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
const HTML_STYLE_ATTR = /\sstyle\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;

function stripCssComments(css: string): string {
	return css.replace(CSS_COMMENT, "");
}

function fontFamilyValuesInCss(css: string): string[] {
	const values: string[] = [];
	for (const match of css.matchAll(FONT_FAMILY_DECL)) {
		const value = match[1]?.trim();
		if (value) {
			values.push(value);
		}
	}
	return values;
}

/**
 * Catalog families named in `font-family` declarations. Comments are ignored.
 * Generics, `inherit`, and `var(--tt-*)` never match the catalog.
 */
export function collectGoogleFontFamiliesFromCss(
	css: string
): GoogleFontFamilyRequest[] {
	const cleaned = stripCssComments(css);
	if (!cleaned.trim()) {
		return [];
	}
	const map = new Map<string, FamilyUse>();
	const blocks = [...cleaned.matchAll(INNERMOST_BLOCK)]
		.map((match) => match[1] ?? "")
		.filter((block) => block.trim().length > 0);
	const sources = blocks.length > 0 ? blocks : [cleaned];
	for (const source of sources) {
		const families: string[] = [];
		for (const value of fontFamilyValuesInCss(source)) {
			for (const family of catalogFamiliesInList(value)) {
				if (!families.includes(family)) {
					families.push(family);
				}
			}
		}
		if (families.length === 0) {
			continue;
		}
		const weight = parseUsedWeight(FONT_WEIGHT_DECL.exec(source)?.[1]);
		recordFamilyUse(map, families, weight, FONT_STYLE_ITALIC.test(source));
	}
	return requestsFromFamilyMap(map);
}

/** `<style>` blocks and `style=""` attributes from an HTML fragment. */
export function cssFromHtmlFontSources(html: string): string {
	if (!html.trim()) {
		return "";
	}
	const chunks: string[] = [];
	for (const match of html.matchAll(HTML_STYLE_BLOCK)) {
		const css = match[1]?.trim();
		if (css) {
			chunks.push(css);
		}
	}
	for (const match of html.matchAll(HTML_STYLE_ATTR)) {
		const value = (match[1] ?? match[2] ?? "").trim();
		if (value && /font-family\s*:/i.test(value)) {
			chunks.push(value);
		}
	}
	return chunks.join("\n");
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string): string {
	const value = record[key];
	return typeof value === "string" ? value : "";
}

const SKIP_CUSTOM_CODE_KEYS = new Set(["parkedCode", "__parkedCode"]);

function appendCustomCodeCss(
	record: Record<string, unknown>,
	chunks: string[]
): void {
	const props = isRecord(record.props) ? record.props : null;
	const isCustomCode =
		record.type === "custom_code" || props?.__builderCustomCode === true;
	if (!isCustomCode) {
		return;
	}
	const codeType = props?.__codeType ?? record.codeType ?? "html";
	if (codeType !== "html") {
		return;
	}
	const css = props
		? readString(props, "__cssCode") || readString(record, "cssCode")
		: readString(record, "cssCode");
	const html = [
		props
			? readString(props, "__sourceCode") || readString(record, "sourceCode")
			: readString(record, "sourceCode"),
		props
			? readString(props, "__staticHtml") || readString(record, "staticHtml")
			: readString(record, "staticHtml"),
	].join("\n");
	if (css.trim()) {
		chunks.push(css);
	}
	const fromHtml = cssFromHtmlFontSources(html);
	if (fromHtml) {
		chunks.push(fromHtml);
	}
}

function walkCustomCodeCss(node: unknown, chunks: string[]): void {
	if (Array.isArray(node)) {
		for (const item of node) {
			walkCustomCodeCss(item, chunks);
		}
		return;
	}
	if (!isRecord(node)) {
		return;
	}
	appendCustomCodeCss(node, chunks);
	for (const [key, value] of Object.entries(node)) {
		if (SKIP_CUSTOM_CODE_KEYS.has(key)) {
			continue;
		}
		walkCustomCodeCss(value, chunks);
	}
}

/**
 * Catalog families in rendered custom_code CSS (`cssCode` / `<style>`) and
 * page settings `customCSSHead` / `customCSSBody` / `customCSSFooter`.
 * Parked code-type buffers are skipped — they are not rendered.
 */
export function collectGoogleFontFamiliesFromRenderedContent(input: {
	docs?: readonly unknown[];
	pageSettings?: unknown;
}): GoogleFontFamilyRequest[] {
	const chunks: string[] = [];
	for (const doc of input.docs ?? []) {
		walkCustomCodeCss(doc, chunks);
	}
	const slots = resolvePageCustomCodeSlots(
		input.pageSettings as Parameters<typeof resolvePageCustomCodeSlots>[0]
	);
	for (const css of [
		slots.customCSSHead,
		slots.customCSSBody,
		slots.customCSSFooter,
	]) {
		if (css.trim()) {
			chunks.push(css);
		}
	}
	return mergeGoogleFontFamilyRequests(
		chunks.map((css) => collectGoogleFontFamiliesFromCss(css))
	);
}

/** One entry per family. Weights and italic are the union, clamped to the catalog. */
export function mergeGoogleFontFamilyRequests(
	groups: readonly (readonly GoogleFontFamilyRequest[])[]
): GoogleFontFamilyRequest[] {
	const map = new Map<string, FamilyUse>();
	for (const group of groups) {
		for (const entry of group) {
			recordFamilyUse(map, [entry.family], null, entry.italic);
			const use = map.get(entry.family);
			if (!use) {
				continue;
			}
			for (const weight of entry.weights) {
				use.weights.add(weight);
			}
		}
	}
	return requestsFromFamilyMap(map);
}

function escapeProbeToken(part: string): string {
	return part.replace(/[\\^$.|?*+()[\]{}]/g, "\\$&");
}

/**
 * Case-insensitive probe for a catalog family inside a `font-family` value.
 * `js` is the unit-test form; `postgres` is the `~*` pattern used by CSP.
 * Same shape so a custom-code-only family is not missed when the proxy
 * cannot see the page being rendered.
 */
export function catalogFontFamilyDeclarationPattern(
	dialect: "js" | "postgres"
): string {
	const space = dialect === "postgres" ? "[[:space:]]" : "\\s";
	const nonAlnum = dialect === "postgres" ? "[^[:alnum:]]" : "[^A-Za-z0-9]";
	const delimiter = dialect === "postgres" ? "[[:space:],'\"(]" : "[\\s,'\"(]";
	const names = [...GOOGLE_FONTS_CATALOG]
		.sort((a, b) => b.length - a.length)
		.map((family) =>
			family
				.trim()
				.split(/\s+/)
				.map((part) => escapeProbeToken(part))
				.join(`${space}+`)
		)
		.join("|");
	// Require `font-family:` so body copy that merely mentions a family
	// (for example "Inter") does not widen CSP. Non-greedy so the name is
	// found inside the declaration, not after it.
	return `font-family${space}*:${space}*(?:[^;}{]*?${delimiter}|${space}*)(?:${names})(?:${nonAlnum}|$)`;
}

/** One css2 stylesheet URL for a single family (`display=swap`). */
export function googleFontsStylesheetHref(
	entry: GoogleFontFamilyRequest
): string {
	const family = entry.family.trim().replace(/ /g, "+");
	const weights = [...entry.weights].sort((a, b) => a - b);
	if (entry.italic) {
		const tuples = [
			...weights.map((weight) => `0,${weight}`),
			...weights.map((weight) => `1,${weight}`),
		].join(";");
		return `https://fonts.googleapis.com/css2?family=${family}:ital,wght@${tuples}&display=swap`;
	}
	return `https://fonts.googleapis.com/css2?family=${family}:wght@${weights.join(";")}&display=swap`;
}

function decodeFamilyToken(token: string): string {
	try {
		return decodeURIComponent(token).replace(/\+/g, " ").trim();
	} catch {
		return token.replace(/\+/g, " ").trim();
	}
}

/** Catalog family names already requested by a `<link>` in HTML. */
export function googleFontFamiliesLinkedInHtml(html: string): Set<string> {
	const found = new Set<string>();
	if (!(html && /fonts\.googleapis\.com/i.test(html))) {
		return found;
	}
	for (const match of html.matchAll(/(?:^|[?&])family=([^&"'#\s]+)/gi)) {
		const raw = match[1] ?? "";
		for (const piece of raw.split("|")) {
			const name = decodeFamilyToken(piece.split(":")[0] ?? "");
			const hit = matchCatalogGoogleFontFamily(name);
			if (hit) {
				found.add(hit);
			}
		}
	}
	return found;
}
